/**
 * calendar/state.js — Shared mutable state and primitives for the calendar page.
 *
 * Every calendar module imports the single `state` object from here and mutates its
 * fields (e.g. `state.calendar = ...`) rather than passing the FullCalendar instance
 * around or stashing it on `window`. esbuild bundles this into one module scope, so the
 * sharing is closure-private — nothing leaks to the global namespace.
 *
 * This module imports nothing, so it sits at the root of the import graph (no cycles).
 */

// The one mutable singleton. Fields are reassigned by core (lifecycle) and the feature
// modules; readers always see the current value because they share this object reference.
export const state = {
    calendar: null,            // the live FullCalendar instance (null when torn down)
    currentEventId: null,      // id of the event open in the detail/reschedule modal
    currentEvent: null,        // the FullCalendar event object currently being viewed
    isDeleting: false,         // guard against double-clicks on delete
    isUpdatingSelection: false, // guard so programmatic select() doesn't re-fire `select`
    filledDays: [],            // YYYY-MM-DD strings marked "day filled" (server-owned)
    feedUrl: '/api/v1/calendar/fullcalendar/events', // overridden from #calendar data-feed-url
    // Whose visits to show: '' (everyone), 'me', 'unassigned', or a user id.
    // Seeded from #calendar data-default-assignee — the SERVER decides where a
    // user lands, this only remembers what they switched to afterwards. Applied
    // by the feed, not by hiding rendered events, so a 30-tech month stays small.
    assignee: '',
};

// localStorage key remembering the last-chosen view (month/week/day/list) per device.
export const CALENDAR_VIEW_KEY = 'calendar_view';

// localStorage key remembering whether Saturday and Sunday are shown ('false' = hidden).
export const CALENDAR_WEEKENDS_KEY = 'calendar_weekends';

// Stable API paths (no server data, so kept in code rather than threaded through data-*).
export const TOGGLE_FILLED_URL = '/calendar/day-filled/toggle';
export const EVENTS_API = '/api/v1/calendar/events';

/**
 * Format a Date as YYYY-MM-DD in LOCAL time.
 * Avoids the UTC shift `toISOString()` introduces near midnight, which would otherwise
 * land an event on the wrong day. Shared by every module that keys days by date string.
 */
export function toLocalDateStr(d) {
    return d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0');
}
